import { useEffect, useState } from 'react';
import {
  PLAN_ROW_STATUSES,
  emptyStrategicPlanRows,
  normalizeStrategicPlanRows,
  type StrategicPlanRow,
} from '@isbd/shared';
import { invokeAiFunction, supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { exportPlanCsv, exportPlanPdf, exportPlanWord } from '@/lib/exportPlan';
import { useToast } from '@/features/ui/toast/useToast';

type PlanKind = 'prep' | 'deploy';

interface Props {
  projectId: string;
  kind: PlanKind;
  /** Mode lecture seule (synthèse) */
  readOnly?: boolean;
}

export function EditableActivityPlan({ projectId, kind, readOnly = false }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const table = kind === 'prep' ? 'preparation_plans' : 'deployment_plans';
  const title = kind === 'prep' ? 'Plan de préparation' : 'Plan de déploiement';
  const generateLabel =
    kind === 'prep' ? 'Générer mon plan de préparation' : 'Générer mon plan de déploiement';
  const [rows, setRows] = useState<StrategicPlanRow[]>(() => emptyStrategicPlanRows(kind));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void supabase
      .from(table)
      .select('activities')
      .eq('project_id', projectId)
      .maybeSingle()
      .then(({ data }) => {
        setRows(normalizeStrategicPlanRows(kind, data?.activities));
      });
  }, [projectId, kind, table]);

  function updateRow(order: number, patch: Partial<StrategicPlanRow>) {
    setRows((prev) =>
      prev.map((r) => (r.order === order ? { ...r, ...patch, axe: r.axe, objectif: r.objectif } : r)),
    );
  }

  async function save(next = rows, opts?: { quiet?: boolean }): Promise<boolean> {
    if (!profile) return false;
    setLoading(true);
    try {
      const { error: err } = await supabase.from(table).upsert(
        {
          user_id: profile.id,
          project_id: projectId,
          activities: next,
        },
        { onConflict: 'project_id' },
      );
      if (err) throw err;
      if (!opts?.quiet) toast.success(`${title} enregistré.`, 'Plan');
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function generate() {
    setLoading(true);
    try {
      const result = await invokeAiFunction<{
        rows?: { order: number; actions: string }[];
        actions?: Record<number, string>;
      }>('ai-analyze', {
        kind: kind === 'prep' ? 'plan_preparation' : 'plan_deployment',
        project_id: projectId,
      });

      const next = emptyStrategicPlanRows(kind).map((row) => {
        const fromArray = result.rows?.find((r) => r.order === row.order);
        const fromMap = result.actions?.[row.order];
        return {
          ...row,
          actions: fromArray?.actions ?? fromMap ?? row.actions,
          // conserver saisies utilisateur déjà faites
          responsable: rows.find((r) => r.order === row.order)?.responsable ?? '',
          echeance: rows.find((r) => r.order === row.order)?.echeance ?? '',
          statut: rows.find((r) => r.order === row.order)?.statut ?? '',
        };
      });
      setRows(next);
      const saved = await save(next, { quiet: true });
      if (saved) {
        toast.success(
          'Actions prioritaires proposées par l’IA. Analysez-les, retenez ce qui convient et complétez responsable, échéance et statut.',
          'Plan',
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Génération impossible');
    } finally {
      setLoading(false);
    }
  }

  const slug = kind === 'prep' ? 'preparation' : 'deploiement';

  return (
    <section className="panel">
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <p className="muted">
        Les colonnes <strong>Axe</strong> et <strong>Objectif</strong> sont fixes. Complétez les actions
        (générables par IA), le responsable, l’échéance et le statut.
      </p>

      {!readOnly ? (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <button type="button" className="btn btn-accent" disabled={loading} onClick={() => void generate()}>
            {loading ? 'IA…' : generateLabel}
          </button>
          <button type="button" className="btn btn-primary" disabled={loading} onClick={() => void save()}>
            Enregistrer
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => exportPlanCsv(title, rows, `plan-${slug}-${projectId.slice(0, 8)}.csv`)}
          >
            Excel (CSV)
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => exportPlanWord(title, rows, `plan-${slug}-${projectId.slice(0, 8)}.doc`)}
          >
            Word
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => exportPlanPdf(title, rows)}>
            PDF
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => exportPlanCsv(title, rows, `plan-${slug}-${projectId.slice(0, 8)}.csv`)}
          >
            Excel (CSV)
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => exportPlanWord(title, rows, `plan-${slug}-${projectId.slice(0, 8)}.doc`)}
          >
            Word
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => exportPlanPdf(title, rows)}>
            PDF
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Axe</th>
              <th>Objectif</th>
              <th>Actions prioritaires</th>
              <th>Responsable</th>
              <th>Échéance</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.order}>
                <td style={{ minWidth: '11rem', fontWeight: 600 }}>{r.axe}</td>
                <td style={{ minWidth: '12rem' }}>{r.objectif}</td>
                <td>
                  {readOnly ? (
                    r.actions || '—'
                  ) : (
                    <textarea
                      value={r.actions}
                      rows={3}
                      onChange={(e) => updateRow(r.order, { actions: e.target.value })}
                      placeholder="Actions prioritaires…"
                    />
                  )}
                </td>
                <td>
                  {readOnly ? (
                    r.responsable || '—'
                  ) : (
                    <input
                      value={r.responsable}
                      onChange={(e) => updateRow(r.order, { responsable: e.target.value })}
                    />
                  )}
                </td>
                <td>
                  {readOnly ? (
                    r.echeance || '—'
                  ) : (
                    <input
                      type="date"
                      value={r.echeance}
                      onChange={(e) => updateRow(r.order, { echeance: e.target.value })}
                    />
                  )}
                </td>
                <td>
                  {readOnly ? (
                    PLAN_ROW_STATUSES.find((s) => s.value === r.statut)?.label || '—'
                  ) : (
                    <select
                      value={r.statut}
                      onChange={(e) =>
                        updateRow(r.order, { statut: e.target.value as StrategicPlanRow['statut'] })
                      }
                    >
                      <option value="">—</option>
                      {PLAN_ROW_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
